import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Login from "./components/Login";
import SignUp from "./components/SignUp";
import ForgotPassword from "./components/ForgotPassword";
import Home from "./pages/Home";
import Profile from "./pages/Profile";
import FamilyHistory from "./components/Home/FamilyHistory";
import Explorer from "./pages/Explorer";
import RequestService from "./pages/RequestService";
import Apply from "./pages/Apply";
import AccountRequest from "./pages/AccountRequest";
import WebForm from "./pages/WebForm";
import ProtectedRoute from "./config/ProtectedRoute";
import Page from "./pages/page"
import { UTILS } from "./config/API";
import UnderConstruction from "./pages/UnderConstruction";
import SqEnroll from "./pages/SqEnroll";
import MyMessages from "./pages/MyMessages"
import MyThread from "./pages/MyThread"
import TaxReturnForm from "./pages/TaxReturnForm"
import TaxReturnPb from "./pages/TaxReturnPb"
import Upload from "./pages/Upload"
import StampsInv from "./components/PbTax/StampsInv";
import NewBusinessLicenceApplication from "./components/PbTaxReturn/NewBusinessLicenceApplication"
import CritBL from "./components/crit/CritBL"
import appConfig, { setThemeColors, getPortalKey } from "./config";
import Registration from "./pages/CCTHRegistration";
import ContactUs from "./pages/ContactUs";
const App = () => {
  const [basename, setBasename] = useState("");
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fetchPortalSettings = async () => {
      try {
        // Check if settings exist in sessionStorage
        const savedSettings = sessionStorage.getItem("portalSettings");
        const savedBase = sessionStorage.getItem("portalBase");

        // Get current path (used for basename comparison)
        const pathSegments = window.location.pathname.split('/');
        const portalSegment = "/" + pathSegments.filter(segment => segment)[0];

        // If we have cached settings and they match the current path, use them
        const settings = savedSettings ? JSON.parse(savedSettings) : null;
        if (savedSettings && savedBase === portalSegment && settings?.ProgramTitle) {

          // Use StyleFolder from cached settings to set theme
          const styleFolder = settings.StyleFolder;
          if (styleFolder) {
            const styleKey = styleFolder.toLowerCase();
            const key =
              appConfig[styleKey]
                ? styleKey
                : appConfig[`${styleKey}portal`]
                  ? `${styleKey}portal`
                  : getPortalKey();
            const config = appConfig[key] || appConfig["portal"];
            if (config && config.name) {
              setThemeColors(config.name);
            }
          } else {
            const key = getPortalKey();
            const config = appConfig[key] || appConfig["portal"];
            if (config && config.name) {
              setThemeColors(config.name);
            }
          }

          setBasename(savedBase);
          if (settings.LoginOptions) {
            sessionStorage.setItem("LoginOptions", settings.LoginOptions);
          }
          setLoading(false);
          return;
        }

        setProgress(0);

        // Fetch new settings
        const response = await fetch(UTILS, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Accept": "application/json"
          },
          body: new URLSearchParams({ action: "settings" }),
        });
        setProgress(60);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setProgress(80);
        console.log(JSON.stringify(data))

        // Set the basename and store in session
        // Prefer current URL segment for routing (e.g. /portal in dev),
        // fall back to VirtualDirectory only if no segment is present.
        const currentSegment = pathSegments.filter(segment => segment)[0];
        const virtualDir = data.VirtualDirectory?.toLowerCase();
        const baseSegment = currentSegment || virtualDir || "portal";
        const newBase = `/${baseSegment}`;

        sessionStorage.setItem("portalSettings", JSON.stringify(data));
        sessionStorage.setItem("portalBase", newBase);
        setBasename(newBase);
        if (data.LoginOptions) {
          sessionStorage.setItem("LoginOptions", data.LoginOptions);
        }

        // Set theme colors using StyleFolder from fresh settings
        if (data.StyleFolder) {
          const styleKey = data.StyleFolder.toLowerCase();
          const key =
            appConfig[styleKey]
              ? styleKey
              : appConfig[`${styleKey}portal`]
                ? `${styleKey}portal`
                : getPortalKey();
          const config = appConfig[key] || appConfig["portal"];
          if (config && config.name) {
            setThemeColors(config.name);
          }
        } else {
          const key = getPortalKey();
          const config = appConfig[key] || appConfig["portal"];
          if (config && config.name) {
            setThemeColors(config.name);
          }
        }
        setProgress(100);

      } catch (error) {
        console.error("Failed to fetch portal settings:", error);
        // Set default values
        const defaultBase = "/portal";
        sessionStorage.setItem("portalSettings", JSON.stringify({
          VirtualDirectory: "portal",
          LoginOptions: "member"
        }));
        sessionStorage.setItem("portalBase", defaultBase);
        setBasename(defaultBase);
        
        // Set default theme colors
        const defaultConfig = appConfig["portal"];
        if (defaultConfig && defaultConfig.name) {
          setThemeColors(defaultConfig.name);
        }
        
        setProgress(100);
      } finally {
        setTimeout(() => setLoading(false), 500);
      }
    };

    // Set initial theme colors based on StyleFolder (with URL fallback)
    const key = getPortalKey();
    const config = appConfig[key] || appConfig["portal"];
    if (config && config.name) {
      setThemeColors(config.name);
    }

    fetchPortalSettings();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen w-screen bg-gradient-to-br from-gray-100 to-gray-200 gap-6 p-6">
        {/* Loading Spinner */}
        <div className="relative flex justify-center items-center">
          <div className="w-16 h-16 border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
          <span className="absolute text-lg font-semibold text-primary">🔄</span>
        </div>

        {/* Loading Text */}
        <h3 className="text-2xl font-semibold text-gray-800">Loading Portal...</h3>

        {/* Progress Bar */}
        <div className="w-full max-w-md h-3 bg-gray-300 rounded-full shadow-md overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-400 to-primary rounded-full transition-all duration-500 ease-in-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Percentage Text with Pulsating Effect */}
        <span className="text-lg font-medium text-gray-700 animate-pulse">{progress}%</span>
      </div>

    );
  }

  return (
    <Router basename={basename || "/portal"}>
      <Routes>
        <Route exact path="/" element={<Navigate to="/login" />} />
        <Route path="/login" element={<Login />} />
        <Route path="/accountRequest" element={<AccountRequest />} />
        <Route path="/blapplication" element={<NewBusinessLicenceApplication />} />
        <Route path="/registration" element={<Registration />} />
        <Route path="/critbl" element={<CritBL />} />
        <Route path="/contactus" element={<ContactUs />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/home" element={<Home />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/family-history" element={<FamilyHistory />} />
          <Route path="/explore" element={<Explorer />} />
          <Route path="/request-service" element={<RequestService />} />
          <Route path="/programs" element={<FamilyHistory />} />
          <Route path="/apply" element={<Apply />} />
          <Route path="/webform" element={<WebForm />} />
          <Route path="/page" element={<Page />} />
          <Route path="/underconstruction" element={<UnderConstruction />} />
          <Route path="/sqenroll" element={<SqEnroll />} />
          <Route path="/mymessages" element={<MyMessages />} />
          <Route path="/taxreturn" element={<TaxReturnForm />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/mythread" element={<MyThread />} />


        </Route>
      </Routes>
    </Router>
  );
};

export default App;